import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Label, Input } from 'reactstrap';
import ActionButton from '../../../../Components/Common/ActionButton';
import {
  ORIGIN_OPTIONS,
  ASSAY_FIELDS,
  YIELD_FIELDS,
} from './registrationConstants';
import {
  deriveSulfurClass,
  deriveYieldClass,
  computeTotalYield,
} from './classification';

// Tab 1 of the Crude Registration wizard — General + Assay Detail + Yield.
// Controlled by the parent shell (form / setForm / errors). "Next" is owned here
// (rule §Create: Button Next) and delegates persistence + validation to onNext.
const CrudeInfoTab = ({
  form,
  setForm,
  errors,
  onNext,
  saving,
  readOnly,
  hideGeneral,
}) => {
  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const totalYield = computeTotalYield(form);
  const sulfurClass = deriveSulfurClass(form.sulfur);
  const yieldClass = deriveYieldClass(totalYield, form.gas, form.hNaphta);

  // Blending compositions (rule §General): default 2 rows, "+" to add more.
  const compositions = form.compositions || [];

  const addComposition = () => {
    setForm(prev => {
      const next = [...(prev.compositions || [])];
      next.push({
        crudeText: `Composition (Crude ${next.length + 1})`,
        composition: '',
      });
      return { ...prev, compositions: next };
    });
  };

  const setComposition = (index, value) => {
    setForm(prev => {
      const next = [...(prev.compositions || [])];
      next[index] = { ...next[index], composition: value };
      return { ...prev, compositions: next };
    });
  };

  const removeComposition = index => {
    setForm(prev => {
      const next = (prev.compositions || []).filter((_, i) => i !== index);
      // Re-label so the numbering stays sequential after a delete.
      const relabelled = next.map((c, i) => ({
        ...c,
        crudeText: `Composition (Crude ${i + 1})`,
      }));
      return { ...prev, compositions: relabelled };
    });
  };

  const toggleBlending = checked => {
    setForm(prev => {
      // Seed the default 2 rows the first time blending is enabled.
      const seeded =
        checked && (!prev.compositions || prev.compositions.length === 0)
          ? [
              { crudeText: 'Composition (Crude 1)', composition: '' },
              { crudeText: 'Composition (Crude 2)', composition: '' },
            ]
          : prev.compositions;
      return { ...prev, isBlending: checked, compositions: seeded };
    });
  };

  const requiredMark = <span className='text-danger'>*</span>;

  return (
    <div>
      <Row>
        {/* ---------- Left column: General ---------- */}
        {/* Hidden in the Edit Assay flow and the read-only Detail expand view
            (hideGeneral) — those only edit/display a single CrudeDetail row,
            not the parent Crude header these fields belong to. */}
        {!hideGeneral && (
          <Col lg={5}>
            <h5 className='text-decoration-underline mb-3'>General</h5>

            <div className='mb-3'>
              <Label className='form-label'>Crude Name {requiredMark}</Label>
              <Input
                type='text'
                maxLength={200}
                disabled={readOnly}
                value={form.crudeName || ''}
                invalid={!!errors.crudeName}
                onChange={e => setField('crudeName', e.target.value)}
              />
              {errors.crudeName && (
                <div className='invalid-feedback d-block'>
                  {errors.crudeName}
                </div>
              )}
            </div>

            <div className='mb-3'>
              <Label className='form-label'>Origin {requiredMark}</Label>
              <Input
                type='select'
                disabled={readOnly}
                value={form.origin || ''}
                invalid={!!errors.origin}
                onChange={e => setField('origin', e.target.value)}
              >
                <option value=''>Select Origin</option>
                {ORIGIN_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
              {errors.origin && (
                <div className='invalid-feedback d-block'>{errors.origin}</div>
              )}
            </div>

            <div className='mb-3'>
              <Label className='form-label'>Country {requiredMark}</Label>
              <Input
                type='text'
                maxLength={100}
                disabled={readOnly}
                value={form.country || ''}
                invalid={!!errors.country}
                onChange={e => setField('country', e.target.value)}
              />
              {errors.country && (
                <div className='invalid-feedback d-block'>{errors.country}</div>
              )}
            </div>

            <div className='mb-3'>
              <Label className='form-label'>Code G5 {requiredMark}</Label>
              <Input
                type='text'
                disabled={readOnly}
                value={form.codeG5 || ''}
                invalid={!!errors.codeG5}
                onChange={e => setField('codeG5', e.target.value)}
              />
              {errors.codeG5 && (
                <div className='invalid-feedback d-block'>{errors.codeG5}</div>
              )}
            </div>

            <div className='mb-3'>
              <Label className='form-label'>
                Production Rate (MBCD) {requiredMark}
              </Label>
              <Input
                type='text'
                disabled={readOnly}
                value={form.productionRate || ''}
                invalid={!!errors.productionRate}
                onChange={e => setField('productionRate', e.target.value)}
              />
              {errors.productionRate && (
                <div className='invalid-feedback d-block'>
                  {errors.productionRate}
                </div>
              )}
            </div>

            <div className='mb-3'>
              <Label className='form-label'>Parcel Size (MB)</Label>
              <Input
                type='text'
                disabled={readOnly}
                value={form.parcelSize || ''}
                onChange={e => setField('parcelSize', e.target.value)}
              />
            </div>

            <div className='mb-3'>
              <Label className='form-label'>Penyaluran {requiredMark}</Label>
              <Input
                type='text'
                disabled={readOnly}
                value={form.penyaluran || ''}
                invalid={!!errors.penyaluran}
                onChange={e => setField('penyaluran', e.target.value)}
              />
              {errors.penyaluran && (
                <div className='invalid-feedback d-block'>
                  {errors.penyaluran}
                </div>
              )}
            </div>

            <div className='form-check mb-3'>
              <Input
                type='checkbox'
                id='isBlending'
                disabled={readOnly}
                checked={!!form.isBlending}
                onChange={e => toggleBlending(e.target.checked)}
              />
              <Label className='form-check-label' for='isBlending'>
                Is Blending ?
              </Label>
            </div>

            {form.isBlending && (
              <div className='mb-3 ps-2 border-start'>
                {compositions.map((c, i) => (
                  <div className='mb-2' key={i}>
                    <Label className='form-label mb-1'>{c.crudeText}</Label>
                    <div className='d-flex align-items-center gap-2'>
                      <Input
                        type='number'
                        step='0.01'
                        disabled={readOnly}
                        value={c.composition}
                        invalid={!!errors[`composition_${i}`]}
                        onChange={e => setComposition(i, e.target.value)}
                      />
                      <span className='text-muted'>%</span>
                      {!readOnly && compositions.length > 1 && (
                        <ActionButton
                          variant='icon'
                          danger
                          size='sm'
                          icon='ri-close-line'
                          label='Remove'
                          confirm={{
                            title: 'Remove composition?',
                            message: `Remove ${c.crudeText || 'this crude'} from the blend composition?`,
                            confirmLabel: 'Yes, remove it',
                          }}
                          onClick={() => removeComposition(i)}
                        />
                      )}
                    </div>
                    {errors[`composition_${i}`] && (
                      <div className='invalid-feedback d-block'>
                        {errors[`composition_${i}`]}
                      </div>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-add-line'
                    onClick={addComposition}
                  >
                    Add Composition
                  </ActionButton>
                )}
              </div>
            )}

            <div className='mb-2'>
              <Label className='form-label text-muted mb-0'>
                Sulfur Classification
              </Label>
              <div className='fw-semibold'>{sulfurClass || '-'}</div>
            </div>
            <div className='mb-2'>
              <Label className='form-label text-muted mb-0'>
                Yield Classification
              </Label>
              <div className='fw-semibold'>{yieldClass || '-'}</div>
            </div>
          </Col>
        )}

        {/* ---------- Right column: Assay Detail + Yield ---------- */}
        <Col
          lg={hideGeneral ? 12 : 7}
          className={hideGeneral ? '' : 'border-start'}
        >
          <h5 className='text-decoration-underline mb-3'>Assay Detail</h5>
          <Row>
            {ASSAY_FIELDS.map(f => (
              <Col md={6} className='mb-3' key={f.key}>
                <Label className='form-label'>
                  {f.label} {f.required && requiredMark}
                </Label>
                <Input
                  type={f.type}
                  step={f.step}
                  min={f.min}
                  max={f.max}
                  disabled={readOnly}
                  value={form[f.key] ?? ''}
                  invalid={!!errors[f.key]}
                  onChange={e => setField(f.key, e.target.value)}
                />
                {errors[f.key] && (
                  <div className='invalid-feedback d-block'>
                    {errors[f.key]}
                  </div>
                )}
              </Col>
            ))}
          </Row>

          <h5 className='text-decoration-underline mb-3 mt-2'>Yield</h5>
          <Row>
            {YIELD_FIELDS.map(f => (
              <Col md={4} className='mb-3' key={f.key}>
                <Label className='form-label'>
                  {f.label} {f.required && requiredMark}
                </Label>
                <div className='d-flex align-items-center gap-1'>
                  <Input
                    type='number'
                    step='0.01'
                    disabled={readOnly}
                    value={form[f.key] ?? ''}
                    invalid={!!errors[f.key]}
                    onChange={e => setField(f.key, e.target.value)}
                  />
                  <span className='text-muted'>%</span>
                </div>
                {errors[f.key] && (
                  <div className='invalid-feedback d-block'>
                    {errors[f.key]}
                  </div>
                )}
              </Col>
            ))}
          </Row>

          <div className='mb-1'>
            <Label className='form-label'>Total Yield {requiredMark}</Label>
            <div className='d-flex align-items-center gap-1'>
              <Input type='text' readOnly value={totalYield} />
              <span className='text-muted'>%</span>
            </div>
            {totalYield !== 100 && (
              <div className='text-warning mt-1'>
                <i className='ri-error-warning-line align-middle me-2'></i>
                Total yield does not equal 100 %
              </div>
            )}
          </div>
        </Col>
      </Row>

      {!readOnly && (
        <div className='d-flex justify-content-end mt-4'>
          <ActionButton variant='primary' disabled={saving} onClick={onNext}>
            {saving ? 'Saving...' : 'Next'}
            <i className='ri-arrow-right-line align-middle ms-2'></i>
          </ActionButton>
        </div>
      )}
    </div>
  );
};

CrudeInfoTab.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  errors: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  readOnly: PropTypes.bool,
  hideGeneral: PropTypes.bool,
};

CrudeInfoTab.defaultProps = {
  errors: {},
  readOnly: false,
  hideGeneral: false,
};

export default CrudeInfoTab;
